import {
  Brain,
  Camera,
  CheckCircle2,
  ChevronRight,
  Coins,
  Database,
  History as HistoryIcon,
  LogOut,
  MessageSquare,
  Settings as SettingsIcon,
  User,
  Users,
} from 'lucide-react'
import { useState } from 'react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { handleUnauthorized } from '@/lib/api-error-handler'
import { BACKEND_URL } from '@/lib/config'
import { useConnectors } from '@/lib/hooks/use-connectors'
import { useCredits } from '@/lib/hooks/use-credits'
import { useSnapshotsEnabled } from '@/lib/hooks/use-snapshots-enabled'
import { usePlatform } from '@/lib/platform'
import { getAuthToken, useSupabaseAuth } from '@/lib/supabase'

interface SettingsProps {
  onOpenConnectors?: () => void
  onOpenCreditsHistory?: () => void
  onOpenMemories?: () => void
}

export default function Settings({
  onOpenConnectors,
  onOpenCreditsHistory,
  onOpenMemories,
}: SettingsProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [isTopUpLoading, setIsTopUpLoading] = useState(false)
  const [feedbackMessage, setFeedbackMessage] = useState('')
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false)
  const { user, isAuthenticated, signOut } = useSupabaseAuth()
  const { credits: balance, isLoading: isLoadingBalance } = useCredits()
  const { connectors } = useConnectors()
  const { snapshotsEnabled, setSnapshotsEnabled } = useSnapshotsEnabled()
  const platform = usePlatform()

  const formattedBalance = (() => {
    if (balance === null || !Number.isFinite(balance)) return null
    if (balance > 0 && balance < 0.01) return '<$0.01'
    return `$${balance.toFixed(2)}`
  })()

  const handleTopUp = async () => {
    const token = await getAuthToken()
    if (!token) return

    setIsTopUpLoading(true)
    setError(null)

    try {
      const response = await fetch(`${BACKEND_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
      })

      if (!response.ok) {
        if (response.status === 401) {
          await handleUnauthorized(response, 'Create checkout session')
          return
        }
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error ||
            errorData.details ||
            `Failed to create checkout session: ${response.status} ${response.statusText}`
        )
      }

      const data = await response.json()

      // Redirect to Stripe checkout
      const checkoutUrl = data.url
      if (checkoutUrl) {
        window.open(checkoutUrl, '_blank')
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to initiate checkout'
      console.error('Top-up error:', errorMessage)
      setError(errorMessage)
    } finally {
      setIsTopUpLoading(false)
    }
  }

  const handleSignOut = async () => {
    setIsSigningOut(true)
    setError(null)
    try {
      await signOut()
      setSuccess(true)
      setTimeout(() => setSuccess(false), 2000)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to sign out'
      console.error('Sign out error:', errorMessage)
      setError(errorMessage)
    } finally {
      setIsSigningOut(false)
    }
  }

  const handleSubmitFeedback = async () => {
    if (!feedbackMessage.trim()) return

    const token = await getAuthToken()
    if (!token) {
      setError('You must be signed in to submit feedback')
      return
    }

    setIsSubmittingFeedback(true)
    setError(null)
    setSuccess(false)

    try {
      const response = await fetch(`${BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ message: feedbackMessage.trim() }),
      })

      if (!response.ok) {
        if (response.status === 401) {
          await handleUnauthorized(response, 'Submit feedback')
          return
        }
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error ||
            `Failed to submit feedback: ${response.status} ${response.statusText}`
        )
      }

      setSuccess(true)
      setFeedbackMessage('')
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error('Feedback submission error:', err)
      setError(err instanceof Error ? err.message : 'Failed to submit feedback')
    } finally {
      setIsSubmittingFeedback(false)
    }
  }

  const handleOpenConnectors = () => {
    setIsOpen(false)
    onOpenConnectors?.()
  }

  const handleOpenCreditsHistory = () => {
    setIsOpen(false)
    onOpenCreditsHistory?.()
  }

  const handleOpenMemories = () => {
    setIsOpen(false)
    onOpenMemories?.()
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" title="Settings">
          <SettingsIcon />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-left">
          <DialogTitle className="text-sm">Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4 overflow-x-hidden">
          {isAuthenticated && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Coins className="size-3 text-muted-foreground" />
                <label className="text-xs font-semibold leading-none">
                  Credits
                </label>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3 p-2 bg-muted/50 rounded-lg border border-border/50">
                  <div className="flex flex-col gap-1">
                    {isLoadingBalance ? (
                      <p className="text-lg font-semibold">Loading...</p>
                    ) : formattedBalance !== null ? (
                      <p className="text-lg font-semibold">
                        {formattedBalance}
                      </p>
                    ) : (
                      <p className="text-lg font-semibold">--</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      title="History"
                      onClick={handleOpenCreditsHistory}
                    >
                      <HistoryIcon />
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleTopUp}
                      disabled={isTopUpLoading}
                    >
                      {isTopUpLoading ? 'Loading...' : 'Top Up'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Connectors Section */}
          {isAuthenticated && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Database className="size-3 text-muted-foreground" />
                <label className="text-xs font-semibold leading-none">
                  Connectors
                </label>
              </div>
              <button
                type="button"
                onClick={handleOpenConnectors}
                className="w-full flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border/50 hover:bg-muted transition-colors text-left"
              >
                <div>
                  <p className="text-sm font-medium">Manage Connectors</p>
                  <p className="text-xs text-muted-foreground">
                    {connectors.length === 0
                      ? 'Connect databases and services'
                      : `${connectors.length} connection${connectors.length !== 1 ? 's' : ''} configured`}
                  </p>
                </div>
                <ChevronRight className="size-4 text-muted-foreground" />
              </button>
            </div>
          )}

          {/* Memories Section */}
          {isAuthenticated && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Brain className="size-3 text-muted-foreground" />
                <label className="text-xs font-semibold leading-none">
                  Memories
                </label>
              </div>
              <button
                type="button"
                onClick={handleOpenMemories}
                className="w-full flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border/50 hover:bg-muted transition-colors text-left"
              >
                <div>
                  <p className="text-sm font-medium">Manage Memories</p>
                  <p className="text-xs text-muted-foreground">
                    View and edit what the AI remembers about you
                  </p>
                </div>
                <ChevronRight className="size-4 text-muted-foreground" />
              </button>
            </div>
          )}

          {/* Snapshots Section */}
          {isAuthenticated && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Camera className="size-3 text-muted-foreground" />
                <label className="text-xs font-semibold leading-none">
                  Snapshots
                </label>
              </div>
              <div className="flex items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg border border-border/50">
                <div>
                  <p className="text-sm font-medium">Enable Snapshots</p>
                  <p className="text-xs text-muted-foreground">
                    Capture spreadsheet state before and after AI actions
                  </p>
                </div>
                <Switch
                  checked={snapshotsEnabled}
                  onCheckedChange={setSnapshotsEnabled}
                />
              </div>
            </div>
          )}

          {/* Feedback Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <MessageSquare className="size-3 text-muted-foreground" />
              <label className="text-xs font-semibold leading-none">
                Feedback
              </label>
            </div>
            <div className="flex flex-col gap-3 p-2 bg-muted/50 rounded-lg border border-border/50">
              <Textarea
                placeholder="We read every feedback!"
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                rows={2}
                disabled={isSubmittingFeedback}
                className="resize-none bg-white text-sm"
              />
              <Button
                type="button"
                onClick={handleSubmitFeedback}
                disabled={isSubmittingFeedback || !feedbackMessage.trim()}
                size="sm"
                className="self-end"
              >
                {isSubmittingFeedback ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </div>

          {/* Discord Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Users className="size-3 text-muted-foreground" />
              <label className="text-xs font-semibold leading-none">
                Community
              </label>
            </div>
            <a
              href="https://discord.gg/m34NPyvd"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border/50 hover:bg-muted transition-colors"
            >
              <img src="/assets/discord.svg" alt="" className="size-5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">Join our Discord</p>
                <p className="text-xs text-muted-foreground">Chat with the team & community</p>
              </div>
              <ChevronRight className="size-4 text-muted-foreground" />
            </a>
          </div>

          {/* Account Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <User className="size-3 text-muted-foreground" />
              <label className="text-xs font-semibold leading-none">
                Account
              </label>
            </div>

            {isAuthenticated && user?.email ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3 p-4 bg-muted/50 rounded-lg border border-border/50 overflow-hidden">
                  <div className="flex items-center gap-3 min-w-0 flex-1 overflow-hidden">
                    <Avatar className="size-10 shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {user.email.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <div className="flex items-center gap-2 mb-1 min-w-0">
                        <p className="text-sm font-medium truncate min-w-0">
                          {user.email}
                        </p>
                        <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        Signed in with Google
                      </p>
                    </div>
                  </div>
                </div>
                {platform !== 'sheets' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSignOut}
                    disabled={isSigningOut}
                    className="shrink-0"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3 p-4 bg-muted/50 rounded-lg border border-border/50">
                <p className="text-sm text-muted-foreground">Not signed in</p>
              </div>
            )}
          </div>

          <div className="space-y-2 min-h-[1.5rem]">
            {error && (
              <p className="text-sm text-destructive flex items-start gap-2 break-words">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive shrink-0 mt-1.5" />
                <span className="break-words">{error}</span>
              </p>
            )}
            {success && (
              <p className="text-sm text-green-600 dark:text-green-500 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>Success!</span>
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
